import { z } from "zod";
import {
  Box,
  Text,
  type WidgetModule,
  type WidgetRenderContext,
} from "@salestrak/ui";

/**
 * `section-header` — a transparent heading widget that titles the widgets below
 * it. Purely presentational (no data hook, no `.graphql`): the render IS the
 * heading, drawn from `config.title` — the same key the widget's built-in
 * rename and the gear dialog's auto-prepended Title field both edit.
 * `chromeless` + `chrome: "headerless"` make the kit chrome paint
 * nothing: the body fills the widget, the whole widget drags, and the action
 * cluster floats top-right on hover. Registered once on the ONE app registry.
 */
export const sectionHeaderConfigSchema = z
  .looseObject({ title: z.string().default("Section") });

export type SectionHeaderConfig = z.infer<typeof sectionHeaderConfigSchema>;

function SectionHeaderRender({ config }: WidgetRenderContext<SectionHeaderConfig>) {
  return (
    <Box direction="column" fill="flex" justify="center">
      <Box border="bottom" paddingBottom="xs">
        <Text size="xl" weight="semibold" truncate={1}>
          {config.title}
        </Text>
      </Box>
    </Box>
  );
}

export const sectionHeaderDef: WidgetModule<SectionHeaderConfig> = {
  type: "section-header",
  title: "Section header",
  icon: "heading-1",
  chromeless: true,
  chrome: "headerless",
  span: { minW: 2, minH: 1, maxW: 12, maxH: 2, defaultW: 12, defaultH: 1 },
  configSchema: sectionHeaderConfigSchema,
  catalog: [
    {
      key: "section-header.default",
      label: "Section header",
      description: "A transparent heading that titles the widgets below it.",
      section: "Custom",
      defaultConfig: { title: "Section" },
      defaultW: 12,
      defaultH: 1,
    },
  ],
  render: SectionHeaderRender,
  // No `settings`/`panel`: the gear dialog's auto-prepended Title field IS the
  // editor — the heading is the title.
};
